/**
 * CUA SO NHIN VAO KHA NANG SUA CHU CUA LOI - va chi mot cua so.
 *
 * Hop dong PdfCore trong packages/types khong co ham "chay thu mot lan sua".
 * Do la dung: hop dong mo ta thu loi PHAI lam duoc, con chay thu la mot tien
 * nghi cua giao dien. Nhung giao dien BAT BUOC phai co no: neu khong, nguoi
 * dung chi biet lan sua bi tu choi sau khi da bam xac nhan.
 *
 * Vi vay ta lam giong het `asPageSource` trong core/index.ts: kiem tra cau truc
 * tai thoi diem chay, tra ve null khi loi khong co kha nang do, va tang tren tu
 * xu ly truong hop null. Khong ep kieu bua, khong gia dinh loi nao dang chay.
 * Khi ban WASM cua loi Rust den ma chua co previewTextReplace, giao dien tu lui
 * ve "khong biet truoc duoc" chu khong vo.
 */
import type { DocumentId, Result, TextRun } from '@pdfeditor/types';
import type { TextReplaceReport } from '../core/edit/applyTextReplace';
import type { TextAlign } from '../core/edit/layout';

export type { TextReplaceReport };
export type { TextAlign };

/** Nhan hien cho nguoi dung. Tieng Viet, co dau. */
export const ALIGN_LABEL: Record<TextAlign, string> = {
  left: 'Căn trái',
  center: 'Căn giữa',
  right: 'Căn phải',
};

/**
 * HINH HOC CUA BIEU TUONG CAN LE - va vi sao no la du lieu chu khong phai ky tu.
 *
 * Truoc day cho nay la ALIGN_GLYPH = { left: '<', center: '=', right: '>' } dung
 * ky tu Unicode ⯇ ≡ ⯈. Mot mui ten doc ra la "di chuyen" hoac "tiep theo", khong
 * doc ra la "can le"; va khong he thong nao bao dam co glyph tu te cho hai ma
 * U+2BC7 / U+2BC8 - chung roi vao phong du phong, moi may mot kieu.
 *
 * Nen bieu tuong duoc VE: mot chong gach ngang, mep so le cho biet can le nao -
 * dung quy uoc cua Word. Du lieu o day la be rong TUONG DOI cua tung dong (0..1
 * cua be ngang o ve). Dong dai va dong ngan xen ke thi mep so le moi doc ra
 * duoc; bon dong bang nhau se thanh mot khoi dac, khong phan biet duoc trai voi
 * phai.
 */
export const ALIGN_ICON_ROW_WIDTHS: readonly number[] = [1, 0.6, 0.86, 0.48];

/**
 * Vi tri hai dau cua mot dong trong o ve, theo he 0..1.
 *
 * Day la CHO DUY NHAT quyet dinh "mep nao thang, mep nao so le". Ve trai thi mep
 * trai thang (start = 0), ve phai thi mep phai thang (end = 1), can giua thi ca
 * hai mep so le deu nhau. Tach ra thanh ham thuan tuy de test kiem duoc bat bien
 * do ma khong can dung ca cay React.
 */
export function alignIconRowExtent(
  align: TextAlign,
  width: number,
): { start: number; end: number } {
  const w = Math.max(0, Math.min(1, width));
  if (align === 'left') return { start: 0, end: w };
  if (align === 'right') return { start: 1 - w, end: 1 };
  return { start: (1 - w) / 2, end: (1 + w) / 2 };
}

// ---------------------------------------------------------------------------
// Co chu: doc mot chuoi nguoi dung go
// ---------------------------------------------------------------------------

/** Gioi han co chu, tinh bang diem. Duoi 4pt khong doc duoc, tren 200pt vo kho. */
export const FONT_SIZE_MIN_PT = 4;
export const FONT_SIZE_MAX_PT = 200;

/**
 * Doc co chu tu o nhap, tra ve null khi KHONG dung duoc.
 *
 * Vi sao tra null chu khong ep ve gia tri gan nhat: o nhap la mot o go tu do, va
 * nguoi dung go tung ky tu mot. Khi ho vua xoa het de go lai, chuoi la rong; khi
 * ho vua go "1" tren duong den "12", so do la 1. Neu ta KEP (clamp) thay vi tu
 * choi, moi buoc go trung gian se ghi mot co chu vo nghia len doi tuong va nguoi
 * dung nhin thay chu nhay lung tung. Tu choi thi doi tuong giu nguyen co cu cho
 * den khi chuoi thanh mot so hop le.
 *
 * Dau phay thap phan duoc chap nhan: ban phim tieng Viet tren Windows cho ra ','
 * chu khong phai '.', va "10,5" ma bi tu choi thi trong nhu loi phan mem.
 */
export function parseFontSizePt(raw: string): number | null {
  const cleaned = raw.trim().replace(',', '.');
  if (cleaned.length === 0) return null;
  // Number('') va Number(' ') deu ra 0, va Number('12abc') ra NaN nhung
  // parseFloat('12abc') ra 12 - nen dung Number() sau khi da chan chuoi rong.
  const v = Number(cleaned);
  if (!Number.isFinite(v)) return null;
  if (v < FONT_SIZE_MIN_PT || v > FONT_SIZE_MAX_PT) return null;
  // Mot chu so thap phan. Hop dong khong can hon, va lam tron o day giu cho o
  // nhap va gia tri ghi vao doi tuong luon la CUNG mot so.
  return Math.round(v * 10) / 10;
}

/** Chuoi hien trong o nhap cho mot co chu. Nghich dao cua parseFontSizePt. */
export function formatFontSizePt(pt: number): string {
  return String(Math.round(pt * 10) / 10);
}

export const ALIGN_SHORTCUT: Record<TextAlign, string> = {
  left: 'Ctrl+Shift+L',
  center: 'Ctrl+Shift+E',
  right: 'Ctrl+Shift+R',
};

export interface TextEditOptions {
  /**
   * Can le. BO QUA = de loi SUY RA tu chu cu (inferAlign trong layout.ts).
   *
   * Bo qua KHONG dong nghia voi 'left'. Mot tieu de can giua duoc ve lai sat mep
   * trai la mot buoc lui nhin thay ngay tren dung loai tai lieu san pham nay
   * phuc vu.
   */
  align?: TextAlign;
  /** CO CHU cho chu MOI, diem. Bo qua = giu co chu do duoc tu chu cu. */
  fontSizePt?: number;
}

export interface TextEditCapableCore {
  /** `scopeId` la ma khoi hoac ma pham vi hep hon - xem core/edit/scope.ts. */
  previewTextReplace(
    doc: DocumentId,
    scopeId: string,
    newText: string,
    options?: TextEditOptions,
  ): Promise<Result<TextReplaceReport>>;
}

export function asTextEditCore(core: unknown): TextEditCapableCore | null {
  const candidate = core as Partial<TextEditCapableCore> | null;
  if (!candidate || typeof candidate.previewTextReplace !== 'function') return null;
  return candidate as TextEditCapableCore;
}

/**
 * Loi dang chay co NHAN duoc tuy chon can le hay khong.
 *
 * Vi sao phai hoi: `options` la tham so THU TU, va mot loi chua biet den no se
 * nhan lai roi bo di - JavaScript khong bao loi khi goi thua tham so. Neu giao
 * dien cu the ma bat nut can le sang thi nguoi dung chon "Căn giữa", thay ban
 * xem truoc doi (vi ban xem truoc doc report.align, ma report.align luc do van
 * la gia tri SUY RA)... va tep xuat ra khong he can giua. Do dung la kieu "thanh
 * cong gia" ma ca tep applyTextReplace.ts duoc viet ra de chan.
 *
 * Phep do la so tham so khai bao cua ham. Tho, nhung no do dung thu can do va no
 * SAI VE PHIA AN TOAN: mot loi co nhan tuy chon ma khai bao thieu tham so se bi
 * coi la khong nhan, tuc la mat mot tinh nang chu khong phai ghi ra tep sai.
 *
 * =============================================================================
 *  HAI DIEU KIEN - CA HAI DA DUOC LAM. Giu chung dung.
 * =============================================================================
 *  Bat duoc nut can le doi HAI dieu kien, khong phai mot. Truoc day khong dieu
 *  kien nao duoc thoa nen ham nay luon tra ve false va ba nut bi khoa xam.
 *
 *    (1) previewTextReplace() nhan tham so `options` va chuyen `align` xuong
 *        preflightTextReplace() -> layoutInBox(). Chi co the thoi la DU cho ban
 *        xem truoc, nhung KHONG du cho tep xuat ra.
 *        -> DA CO: PdfCoreBrowser.previewTextReplace(doc, scopeId, newText,
 *           options) chuyen align xuong prepareTextReplace().
 *
 *    (2) `align` phai di theo duoc vao EditCommand. Duong luu phat lai danh sach
 *        lenh (App.handleSave -> core.save), nen mot lua chon chi song trong
 *        trang thai giao dien se bien mat truoc khi byte duoc ghi.
 *        -> DA CO, va do la mot THAY DOI HOP DONG: bien the 'text.replace' trong
 *           packages/types nay co `align?: TextAlign`. De TUY CHON nen moi lenh
 *           cu van hop le va invert/coalesce trong state/commands.ts van dung;
 *           vang mat thi duong ghi SUY RA nhu truoc.
 *        -> Duong day day du: App.handleCommitEdit gan align vao lenh ->
 *           PdfCoreBrowser.apply() kiem bang chinh align do -> save() chuyen no
 *           vao applyTextReplace() -> preflight -> layoutInBox().
 *
 *  Lam (1) ma quen (2) con te hon la chua lam gi ca: ban xem truoc can giua ma
 *  tep xuat ra can trai chinh la kieu "thanh cong gia" so 2 trong
 *  applyTextReplace.ts, chi doi truc.
 *
 *  PHEP DO VAN LA SO THAM SO KHAI BAO, va no van chi nhin thay (1). No khong
 *  the kiem tra (2) - khong co cach nao hoi mot doi tuong dang chay xem lenh cua
 *  no mang duoc truong gi. Cai giu (2) khong bi go mat la bo test:
 *  test/textAlign.test.ts ("lua chon tuong minh phai thang phep suy ra tren
 *  duong GHI") va test/commands.test.ts (align song sot qua invert/coalesce).
 *  Neu ai do go truong `align` khoi hop dong, hai test do do truoc khi nguoi
 *  dung do.
 */
export function coreAcceptsAlign(core: unknown): boolean {
  const candidate = asTextEditCore(core);
  if (!candidate) return false;
  return candidate.previewTextReplace.length >= 4;
}

/**
 * Van ban goc cua mot day run.
 *
 * Dinh nghia song trong core/edit/scope.ts va duoc XUAT LAI o day, khong chep
 * lai: EditScope.text duoc dung tu chinh ham do, va tang giao dien phai doc ra
 * DUNG chuoi ay khi dien san o soan va khi dat previousText cho lenh. Hai chuoi
 * lech nhau mot ky tu la Ctrl+Z de lai mot mang che trang mang noi dung y het
 * ban goc.
 */
export { runsPlainText } from '../core/edit/scope';

/** Co chu tieu bieu - trung vi, ben vung truoc vai run lac loai. */
export function runsFontSizePt(runs: readonly TextRun[]): number {
  const sizes = runs.map((r) => r.fontSizePt).filter((s) => s > 0).sort((a, b) => a - b);
  if (sizes.length === 0) return 0;
  return sizes[Math.floor(sizes.length / 2)] ?? 0;
}

/** Mau chu tieu bieu - lay tu run dau tien co chu. */
export function runsColorRgb(runs: readonly TextRun[]): readonly [number, number, number] {
  const run = runs.find((r) => r.text.length > 0);
  return run ? run.colorRgb : [0, 0, 0];
}

/*
 * Ba ham tren nhan RUN chu khong nhan KHOI.
 *
 * Truoc day chung nhan TextBlock, va do chinh la cho giao dien am tham gia dinh
 * "don vi duoc sua = ca khoi". Tu khi co viec chon theo pham vi, don vi do co
 * the la mot run, mot dong, hoac ca khoi (core/edit/scope.ts) - va ca ba deu
 * chi la mot danh sach run. Nhan dung thu ma ta thuc su can khien khong con cho
 * nao co the vo tinh lay lai hinh hoc cua khoi cha.
 */

/**
 * Doi mau sang chuoi #rrggbb cho <input type="color"> va nguoc lai.
 *
 * MAU O DAY LA NOI DUNG, khong phai mau giao dien: no di thang vao mau chu cua
 * doi tuong duoc ghi ra tep. Khong bien nao cua chu de sang/toi duoc phep cham
 * vao no - doi giao dien sang mau thi hop chu cung phai giu dung mau nguoi dung
 * chon, vi trang giay trong tep khong doi mau theo giao dien.
 */
export function hexColor(rgb: readonly [number, number, number]): string {
  const c = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v * 255)))
      .toString(16)
      .padStart(2, '0');
  return `#${c(rgb[0])}${c(rgb[1])}${c(rgb[2])}`;
}

export function rgbFromHex(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);
  if (!Number.isFinite(n)) return [0, 0, 0];
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

export function cssColor(rgb: readonly [number, number, number]): string {
  const to255 = (v: number) => Math.max(0, Math.min(255, Math.round(v * 255)));
  return `rgb(${to255(rgb[0])}, ${to255(rgb[1])}, ${to255(rgb[2])})`;
}

// ---------------------------------------------------------------------------
// Dich ket qua chay thu sang ngon ngu nguoi dung hieu
// ---------------------------------------------------------------------------

export type Verdict = 'ok' | 'ok-substituted' | 'blocked' | 'idle';

export interface EditVerdict {
  verdict: Verdict;
  /** Mot dong ngan, in dam. */
  headline: string;
  /** Ly do day du tu loi - khong bao gio bia them, khong bao gio cat bot. */
  detail: string;
  /** Viec nguoi dung co the lam de go vuong. Rong khi khong co viec gi lam duoc. */
  fix: string;
  /**
   * Cac ky tu THUC SU thieu glyph, lay tu ma U+XXXX trong ly do cua loi.
   * Rong khi lan tu choi khong phai vi thieu glyph.
   */
  missingChars: string[];
}

/**
 * Tieu de va huong xu ly cho tung ly do tu choi.
 *
 * `detail` luon lay nguyen van tu report.reason: loi la nguon su that, giao dien
 * khong duoc dien giai lai. O day ta chi them tieu de ngan va mot loi khuyen.
 */
const OUTCOME_COPY: Record<string, { headline: string; fix: string }> = {
  'thanh-cong': { headline: 'Sửa được', fix: '' },
  'khoi-khong-co-hinh-hoc': {
    headline: 'Khối này không đặt chữ được',
    fix: 'Hãy chọn một khối khác — khối này không có đủ dữ liệu vị trí để đặt chữ mới.',
  },
  'van-ban-khong-doi': {
    headline: 'Chưa có nội dung mới',
    fix: 'Hãy gõ nội dung thay thế. Để trống không xóa được chữ cũ (cách sửa này là vẽ đè).',
  },
  'tu-choi-font': {
    headline: 'Không phân giải được font',
    fix: 'Không có font nào — kể cả Noto Sans đóng gói sẵn — dùng được cho khối này.',
  },
  'tu-choi-thieu-glyph': {
    headline: 'Font thiếu glyph cho ký tự bạn gõ',
    fix: 'Hãy đổi hoặc bỏ những ký tự bên dưới. Nếu vẫn ghi, chúng sẽ thành ô vuông trống.',
  },
  'tu-choi-trang-xoay': {
    headline: 'Trang này bị xoay',
    fix: 'Bản web từ chối sửa trang xoay thay vì ghi ra chữ nằm sai chỗ. Cần lõi Rust/PDFium.',
  },
  'tu-choi-khoi-ngoai-trang': {
    headline: 'Khối này rộng hơn cả trang',
    // Gap gan nhu chac chan tren tai lieu dang bang: viec gom nhom keo ca hang
    // vao mot khoi rong hon kho trang. Nguoi dung thuong dang o nac "Cả khối",
    // nen loi khuyen "chon khoi nho hon" la vo nghia - phai chi dung cai nut.
    fix: 'Thường gặp ở bảng: các ô trong một hàng bị gom thành một khối rộng quá khổ trang. Hãy bấm "Thu hẹp" (hoặc Alt+↓) để về phạm vi Dòng hoặc Ô — sửa từng ô vẫn hoạt động bình thường.',
  },
  'tu-choi-mau-trung-nen': {
    headline: 'Chữ mới sẽ vô hình',
    fix: 'Màu chữ trùng màu nền che. Tệp sẽ hợp lệ nhưng bạn chỉ nhìn thấy một mảng trắng.',
  },
  'tu-choi-khong-vua-khoi': {
    headline: 'Văn bản không vừa khối',
    fix: 'Hãy rút ngắn nội dung. Việc sửa chỉ được dồn chữ TRONG khối (ADR-005).',
  },
  'trang-ngoai-pham-vi': {
    headline: 'Trang không tồn tại',
    fix: 'Tài liệu có thể đã được mở lại. Hãy chọn lại khối cần sửa.',
  },
  'khong-mo-duoc-de-ghi': {
    headline: 'Không mở được tệp để ghi',
    fix: 'Tệp có thể được mã hóa theo cách bản web chưa xử lý được.',
  },
  'khong-nhung-duoc-font': {
    headline: 'Không nhúng được font',
    fix: 'Font nguồn có bảng không chuẩn. Không thể ghi chữ mới mà không có font.',
  },
};

/**
 * Rut cac ky tu thieu glyph ra tu ly do cua loi.
 *
 * describeMissing() trong core CO CHU DICH chi tra ve ma U+XXXX chu khong tra
 * ve ky tu: chuoi do di vao nhat ky, va nhat ky khong duoc chua noi dung tai
 * lieu. O day thi khac - chu ta hien la chu NGUOI DUNG VUA GO, dang nam ngay
 * trong o soan ben canh. Hien ma U+1EBF cho nguoi dung Viet Nam la vo dung;
 * hien "ế" moi la cau tra loi.
 */
export function extractMissingChars(reason: string): string[] {
  const out: string[] = [];
  const re = /U\+([0-9A-Fa-f]{4,6})/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(reason)) !== null) {
    const cp = Number.parseInt(m[1] ?? '', 16);
    if (!Number.isFinite(cp) || cp <= 0 || cp > 0x10ffff) continue;
    try {
      out.push(String.fromCodePoint(cp));
    } catch {
      // Ma khong hop le (surrogate le loi). Bo qua - tha thieu con hon hien rac.
    }
  }
  return out;
}

export function readVerdict(report: TextReplaceReport | null): EditVerdict {
  if (!report) {
    return { verdict: 'idle', headline: '', detail: '', fix: '', missingChars: [] };
  }
  const copy = OUTCOME_COPY[report.outcome] ?? {
    headline: 'Không sửa được',
    fix: '',
  };
  const missingChars =
    report.outcome === 'tu-choi-thieu-glyph' ? extractMissingChars(report.reason) : [];

  if (!report.ok) {
    return {
      verdict: 'blocked',
      headline: copy.headline,
      detail: report.reason,
      fix: copy.fix,
      missingChars,
    };
  }

  return {
    verdict: report.substituted ? 'ok-substituted' : 'ok',
    headline: report.substituted ? 'Sửa được — nhưng phải thay font' : 'Sửa được',
    detail: report.reason,
    fix: '',
    missingChars: [],
  };
}
